// The one registry of "needs you" items (issue 713): key -> sentence, link text,
// href. user-api decides WHICH items are open and fixable by this person; this file
// only knows how to word and route the ones the UI has heard of. A key it does not
// know is dropped (not counted, not rendered), so a new server-side item can ship
// before its UI and a new item here is an app-kit-only change.

export interface NeedsYouItemView {
  key: string;
  /** Lone strip only — the legal item keeps its heading line word for word. */
  title?: string;
  /** The full sentence a lone strip carries. */
  message: string;
  /** The button of a lone strip. */
  ctaLabel: string;
  /** The link text when the strip lists several items. */
  linkLabel: string;
  href: string;
}

export interface NeedsYouContext {
  t: (key: string) => string;
  workspaceUrl: string;
  /** Where billing lives for this app (see buildWorkspaceBillingHref); '' = cannot be routed. */
  billingHref: string;
}

type RawItem = {
  key?: unknown;
  employee?: { open?: unknown[] } | null;
  candidate?: { open?: unknown[]; imprintMissing?: unknown } | null;
  missing?: unknown;
};

function legalItem(raw: RawItem, ctx: NeedsYouContext): NeedsYouItemView | null {
  const hasEmployee = (raw.employee?.open?.length ?? 0) > 0;
  const hasCandidate = (raw.candidate?.open?.length ?? 0) > 0;
  // The imprint has no blanks, but the portal stays offline without it (issue
  // 766): user-api keeps the item open for it, and the sentence names it.
  const imprintMissing = raw.candidate?.imprintMissing === true;
  if (!hasEmployee && !hasCandidate && !imprintMissing) return null;
  const parts: string[] = [];
  if (hasEmployee) parts.push(ctx.t('legalNoticeBanner.employeeBody'));
  if (hasCandidate && imprintMissing) parts.push(ctx.t('legalNoticeBanner.candidateImprintBody'));
  else if (hasCandidate) parts.push(ctx.t('legalNoticeBanner.candidateBody'));
  else if (imprintMissing) parts.push(ctx.t('legalNoticeBanner.imprintBody'));
  return {
    key: 'legal_notices',
    title: ctx.t('legalNoticeBanner.title'),
    message: parts.join(' '),
    ctaLabel: ctx.t('legalNoticeBanner.cta'),
    linkLabel: ctx.t('needsYou.legal.link'),
    href: `${ctx.workspaceUrl.replace(/\/$/, '')}/admin/legal-notices`,
  };
}

// The billing item is always about invoice details or the card, and both live on the
// billing page's Zahlungsmethode pane — the page itself opens on Pläne (issue 1000),
// so the link names its pane. Any query the host put on the href (session params)
// is kept; an href that cannot be parsed is passed through as it came.
function onPaymentMethodTab(href: string): string {
  if (!href) return href;
  try {
    const absolute = /^[a-z][a-z0-9+.-]*:/i.test(href);
    const url = new URL(href, 'https://relative.invalid');
    url.searchParams.set('tab', 'paymentMethod');
    return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

function billingItem(raw: RawItem, ctx: NeedsYouContext): NeedsYouItemView | null {
  const missing = Array.isArray(raw.missing) ? raw.missing : [];
  const invoice = missing.includes('invoice_details');
  const payment = missing.includes('payment_method');
  if (!invoice && !payment) return null;
  const bodyKey = invoice && payment ? 'bodyBoth' : invoice ? 'bodyInvoiceDetails' : 'bodyPaymentMethod';
  return {
    key: 'billing_setup',
    message: ctx.t(`needsYou.billing.${bodyKey}`),
    ctaLabel: ctx.t('needsYou.billing.cta'),
    linkLabel: ctx.t('needsYou.billing.link'),
    href: onPaymentMethodTab(ctx.billingHref),
  };
}

// The server sends `{ key: 'time_rules' }` only while the workspace has neither
// confirmed its statutory rules nor made a deviation of its own, and only to a person
// who can act on it. The page itself lives in the workspace app.
function timeRulesItem(_raw: RawItem, ctx: NeedsYouContext): NeedsYouItemView {
  return {
    key: 'time_rules',
    message: ctx.t('needsYou.timeRules.body'),
    ctaLabel: ctx.t('needsYou.timeRules.cta'),
    linkLabel: ctx.t('needsYou.timeRules.link'),
    href: `${ctx.workspaceUrl.replace(/\/$/, '')}/admin/statutory-rules`,
  };
}

const REGISTRY: Record<string, (raw: RawItem, ctx: NeedsYouContext) => NeedsYouItemView | null> = {
  legal_notices: legalItem,
  billing_setup: billingItem,
  time_rules: timeRulesItem,
};

export function describeNeedsYouItems(items: unknown, ctx: NeedsYouContext): NeedsYouItemView[] {
  if (!Array.isArray(items)) return [];
  const out: NeedsYouItemView[] = [];
  for (const raw of items as RawItem[]) {
    const describe = typeof raw?.key === 'string' && Object.prototype.hasOwnProperty.call(REGISTRY, raw.key) ? REGISTRY[raw.key] : null;
    const view = describe?.(raw, ctx);
    if (view) out.push(view);
  }
  return out;
}
